import React, { useState, useCallback, useEffect } from 'react';
import { Table, Button, TextInput, Modal, ModalHeader, ModalBody, ModalFooter, TableSize } from '@ui';
import { Text } from '@ui/atoms/Text';
import { translate } from '@utils/i18n';
import { useSearchUsers } from '../../hooks/useSearchUsers';
import './UserAccessModalStyles.scss';
const namespace = 'features.settings.ip_assistant';
const SORTABLE_COLUMNS = ['name', 'username', 'role'];
function buildSelectionKey(userType, id) {
    return `${userType}:${id}`;
}
function parseSelectionKey(selectionKey) {
    const [userType, id] = selectionKey.split(':');
    if ((userType !== 'user' && userType !== 'technician') || !id)
        return null;
    const numericId = parseInt(id, 10);
    if (Number.isNaN(numericId))
        return null;
    return { userType, id: numericId };
}
function UserAccessModal({ isOpen, onClose, onSave, allowedUserIds, allowedTechnicianIds, maxUsers, isSaving }) {
    var _a;
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState('name');
    const [sortDir, setSortDir] = useState('asc');
    const { data: usersData, isLoading } = useSearchUsers(debouncedQuery, page, { sortBy, sortDirection: sortDir });
    const users = (usersData === null || usersData === void 0 ? void 0 : usersData.users) || [];
    const meta = usersData === null || usersData === void 0 ? void 0 : usersData.meta;
    useEffect(() => {
        if (isOpen) {
            setSelectedIds([
                ...allowedUserIds.map((id) => buildSelectionKey('user', id)),
                ...allowedTechnicianIds.map((id) => buildSelectionKey('technician', id))
            ]);
            setSearchQuery('');
            setDebouncedQuery('');
            setPage(1);
            setSortBy('name');
            setSortDir('asc');
        }
    }, [isOpen, allowedUserIds, allowedTechnicianIds]);
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(searchQuery);
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);
    const handleSearchChange = useCallback((e) => {
        setSearchQuery(e.target.value);
    }, []);
    const handleSelectionChange = useCallback((itemId, checked) => {
        const selectionKey = String(itemId);
        if (checked) {
            setSelectedIds((prev) => (prev.includes(selectionKey) ? prev : [...prev, selectionKey]));
        }
        else {
            setSelectedIds((prev) => prev.filter((id) => id !== selectionKey));
        }
    }, []);
    const handleSelectAll = useCallback(() => {
        const visibleIds = users.map((user) => buildSelectionKey(user.userType, user.id));
        const visibleIdsSet = new Set(visibleIds);
        // Check if all visible items are currently selected
        const allVisibleSelected = visibleIds.every((id) => selectedIds.includes(id));
        // Toggle behavior: if all visible are selected, unselect them; otherwise, select them
        if (allVisibleSelected) {
            // Unselect all visible items
            setSelectedIds((prev) => prev.filter((id) => !visibleIdsSet.has(id)));
        }
        else {
            // Select all visible items (merge with existing selections)
            setSelectedIds((prev) => {
                const newIds = new Set([...prev, ...visibleIds]);
                return Array.from(newIds);
            });
        }
    }, [users, selectedIds]);
    const handleSort = useCallback((dataKey, direction) => {
        setSortBy(dataKey);
        setSortDir(direction);
        setPage(1);
    }, []);
    const handleSave = useCallback(() => {
        const userIds = [];
        const technicianIds = [];
        selectedIds.forEach((selectionKey) => {
            const parsedKey = parseSelectionKey(selectionKey);
            if (!parsedKey)
                return;
            if (parsedKey.userType === 'user') {
                userIds.push(parsedKey.id);
            }
            else {
                technicianIds.push(parsedKey.id);
            }
        });
        void onSave(userIds, technicianIds);
    }, [onSave, selectedIds]);
    const initialCount = allowedUserIds.length + allowedTechnicianIds.length;
    const hadUsersInitially = initialCount > 0;
    // Mirrors the server rule: only a save that grows the count is capped, so a
    // tenant already over its licenses can still remove people.
    const isOverLicenseLimit = selectedIds.length > maxUsers && selectedIds.length > initialCount;
    const isRemovingAll = hadUsersInitially && selectedIds.length === 0;
    const getSaveLabel = () => {
        if (isRemovingAll)
            return translate('remove_users', { namespace });
        if (hadUsersInitially)
            return translate('update_users', { namespace });
        return translate('add_users', { namespace });
    };
    const saveLabel = getSaveLabel();
    const modalTitle = hadUsersInitially
        ? translate('update_users_title', { namespace })
        : translate('select_users_title', { namespace });
    const columns = [
        { dataKey: 'name', label: translate('column_name', { namespace }) },
        { dataKey: 'username', label: translate('column_username', { namespace }) },
        { dataKey: 'role', label: translate('column_role', { namespace }) }
    ];
    const getCountText = () => {
        if (isOverLicenseLimit)
            return translate('licenses_exceeded', { namespace, count: selectedIds.length, max: maxUsers });
        if (selectedIds.length === 0)
            return translate('select_at_least_one', { namespace });
        return translate('users_selected', { namespace, count: selectedIds.length, max: maxUsers });
    };
    const countText = getCountText();
    return (React.createElement(Modal, { isOpen: isOpen, onClose: onClose, ariaLabel: modalTitle, className: "ip-assistant-user-access-modal" },
        React.createElement(ModalHeader, { title: modalTitle, onClose: onClose }),
        React.createElement(ModalBody, null,
            React.createElement("div", { className: "ip-assistant-user-access-modal__search" },
                React.createElement(TextInput, { id: "ip-assistant-user-search", label: translate('search_placeholder', { namespace }), value: searchQuery, onChange: handleSearchChange })),
            !isLoading && users.length === 0 && (React.createElement(Text, { variant: "body", className: "ip-assistant-user-access-modal__no-results" }, translate('no_results', { namespace }))),
            !isLoading && users.length > 0 && (React.createElement(Table, { data: users, columns: columns, size: TableSize.Full, variant: "listing", selectable: true, selectedItems: selectedIds, onSelectionChange: handleSelectionChange, onSelectAll: handleSelectAll, sortable: true, sortableColumns: SORTABLE_COLUMNS, sortBy: sortBy, sortDirection: sortDir, onSort: handleSort, onPageChange: setPage, serverCurrentPage: meta === null || meta === void 0 ? void 0 : meta.currentPage, serverTotalPages: meta === null || meta === void 0 ? void 0 : meta.totalPages, serverTotalItems: meta === null || meta === void 0 ? void 0 : meta.totalCount, pageSize: (_a = meta === null || meta === void 0 ? void 0 : meta.perPage) !== null && _a !== void 0 ? _a : 10, getItemId: (user) => buildSelectionKey(user.userType, user.id) }))),
        React.createElement(ModalFooter, { startActions: React.createElement(Text, { variant: "body", className: selectedIds.length === 0 || isOverLicenseLimit
                    ? 'ip-assistant-user-access-modal__footer-count--warning'
                    : '' }, countText) },
            React.createElement(Button, { onClick: onClose }, translate('cancel', { namespace: 'generic' })),
            React.createElement(Button, { variant: "primary", onClick: handleSave, disabled: (!hadUsersInitially && selectedIds.length === 0) || isOverLicenseLimit || isSaving }, saveLabel))));
}
export default UserAccessModal;
